import {
  useQuery,
  UseQueryResult,
  useMutation,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query";
import axiosInstance from "../common/axiosInstance";
import {
  OpportunitiesParams,
  OpportunitiesResponse,
  OpportunityDetailsBody,
  OpportunityDetailsQueryParams,
  OpportunityDetailsResponse,
  RefreshSalesforceOpportunityMappingParams,
} from "../../interfaces/opportunities";

export const PATHS = Object.freeze({
  GET_SUB_ORG_OPPORTUNITIES: "/sub-org-opportunities",
  GET_OPPORTUNITY_DETAILS: `/opportunity-details`,
  REFRESH_SALESFORCE_OPPORTUNITY_MAPPING: "/analytics/salesforce-opportunity-mapping/refresh",
});

const getSubOrgOpportunities = async (params:OpportunitiesParams): Promise<OpportunitiesResponse> => {
  const response = await axiosInstance.post(PATHS.GET_SUB_ORG_OPPORTUNITIES,
    {
      ...(params.closeDateStart && { closeDateStart: params.closeDateStart }),
      ...(params.closeDateEnd && { closeDateEnd: params.closeDateEnd }),
      ...(params.employeePorId && { employeePorId: params.employeePorId }),
      ...(params.opportunityIds && params.opportunityIds.length > 0 && { opportunityIds: params.opportunityIds }),
      ...(params.dealStages && params.dealStages.length > 0 && { dealStages: params.dealStages }),
      ...(params.managerForecasts && params.managerForecasts.length > 0 && { managerForecasts: params.managerForecasts }),
      ...(params.subOrgIds && params.subOrgIds.length > 0 && { subOrgIds: params.subOrgIds }),
      ...(params.opportunityOwners && params.opportunityOwners.length > 0 && { opportunityOwners: params.opportunityOwners }),
      ...(params.arrAmountMin !== undefined && params.arrAmountMin !== null && { arrAmountMin: params.arrAmountMin }),
      ...(params.arrAmountMax !== undefined && params.arrAmountMax !== null && { arrAmountMax: params.arrAmountMax }),
      ...(params.nextMeetingDateRange?.startTime && params.nextMeetingDateRange?.endTime && {
        nextMeetingDateRange: params.nextMeetingDateRange,
      }),
    },
    {
    params: {
      limit: params.limit,
      offset: params.offset,
      sortOrder: params.sortOrder,
      sortBy: params.sortBy,
      ...(params.impersonatingPorId && { impersonatingPorId: params.impersonatingPorId }),
      ...(params.accessLevel && { accessLevel: params.accessLevel }),
    }
    });
  return response.data;
};

const useGetSubOrgOpportunities = (
  params: OpportunitiesParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery({
    queryKey: ["get_sub_org_opportunities", stringifiedParams],
    queryFn: () => getSubOrgOpportunities(params),
    enabled,
    retry: 0,
  });
};

const getOpportunityDetails = async (
  params: OpportunityDetailsQueryParams,
  body: OpportunityDetailsBody
): Promise<OpportunityDetailsResponse> => {
  const response = await axiosInstance.post(
    PATHS.GET_OPPORTUNITY_DETAILS,
    body,
    {
      params: {
        limit: params.limit,
        offset: params.offset,
        sortOrder: params.sortOrder,
        sortBy: params.sortBy,
        mappingId: params.mappingId,
        ...(params.impersonatingPorId && { impersonatingPorId: params.impersonatingPorId }),
        ...(params.accessLevel && { accessLevel: params.accessLevel }),
      },
    }
  );
  return response.data;
};

const useGetOpportunityDetails = (
  params: OpportunityDetailsQueryParams,
  body: OpportunityDetailsBody,
  enabled: boolean,
): UseQueryResult<OpportunityDetailsResponse, any> => {
  const stringifiedParams = JSON.stringify(params);
  const stringifiedBody = JSON.stringify(body);
  return useQuery({
    queryKey: ["get_opportunity_details", stringifiedParams, stringifiedBody],
    queryFn: () => getOpportunityDetails(params, body),
    enabled: enabled && !!params.mappingId,
    retry: 0,
  });
};

const refreshSalesforceOpportunityMapping = async (
  params?: RefreshSalesforceOpportunityMappingParams
): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.REFRESH_SALESFORCE_OPPORTUNITY_MAPPING,
    {},
    {
      params: {
        ...(params?.sf_account_id && {
          sf_account_id: params.sf_account_id,
        }),
        ...(params?.impersonatingPorId && {
          impersonatingPorId: params.impersonatingPorId,
        }),
        ...(params?.accessLevel && {
          accessLevel: params.accessLevel,
        }),
      },
    }
  );

  return response.data;
};

const useRefreshSalesforceOpportunityMapping = (
  options?: UseMutationOptions<any, any, RefreshSalesforceOpportunityMappingParams | void>
): UseMutationResult<any, any, RefreshSalesforceOpportunityMappingParams | void> => {
  return useMutation({
    mutationFn: (params?: RefreshSalesforceOpportunityMappingParams | void) =>
      refreshSalesforceOpportunityMapping(params || undefined),
    ...options,
  });
};

export interface OpportunitiesService {
  readonly useGetSubOrgOpportunities: (
    params: OpportunitiesParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;
  readonly useGetOpportunityDetails: (
    params: OpportunityDetailsQueryParams,
    body: OpportunityDetailsBody,
    enabled: boolean
  ) => UseQueryResult<OpportunityDetailsResponse, any>;
  readonly useRefreshSalesforceOpportunityMapping: (
    options?: UseMutationOptions<any, any, RefreshSalesforceOpportunityMappingParams | void>
  ) => UseMutationResult<any, any, RefreshSalesforceOpportunityMappingParams | void>;
};

const OpportunitiesService: OpportunitiesService = Object.freeze({
  useGetSubOrgOpportunities,
  useGetOpportunityDetails,
  useRefreshSalesforceOpportunityMapping,
});

export default OpportunitiesService;
